import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { Loader2, Copy, Check, Trash2, Plus, Link as LinkIcon } from 'lucide-react';
import { automationService, type ScenarioShareLink } from '@/services/strategyService';

// Expiry presets — the API has no default lifetime, the owner must pick one.
const EXPIRY_PRESETS: { value: string; label: string; days: number }[] = [
  { value: '1', label: '1 day', days: 1 },
  { value: '7', label: '7 days', days: 7 },
  { value: '30', label: '30 days', days: 30 },
];

// Build the recipient-facing URL for a token. #/shared-scenario is a PUBLIC
// route (outside the dashboard auth gate) so a recipient without an account
// can open it; the page resolves ?key=… via the anonymous-safe by-share
// endpoint.
function shareUrlFor(token: string): string {
  return `${window.location.origin}${window.location.pathname}` +
    `#/shared-scenario?key=${encodeURIComponent(token)}`;
}

// Copy text to the clipboard, with a legacy execCommand fallback for plain-HTTP
// origins where navigator.clipboard is unavailable. Returns success.
async function copyText(text: string): Promise<boolean> {
  if (window.isSecureContext && navigator.clipboard?.writeText) {
    try { await navigator.clipboard.writeText(text); return true; }
    catch { /* fall through to legacy path */ }
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch { return false; }
}

function fmtExpiry(iso: string): string {
  const d = new Date(iso);
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(d);
}

interface Props {
  runId: string;
  scenarioName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ShareScenarioDialog: React.FC<Props> = ({ runId, scenarioName, open, onOpenChange }) => {
  const [links, setLinks] = useState<ScenarioShareLink[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preset, setPreset] = useState<string>('7');
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  // (Re)load existing links whenever the dialog opens for a run.
  useEffect(() => {
    if (!open || !runId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    automationService.listScenarioShares(runId)
      .then(ls => { if (!cancelled) setLinks(ls); })
      .catch(err => { if (!cancelled) setError(err.message || String(err)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, runId]);

  const createLink = async () => {
    const days = EXPIRY_PRESETS.find(p => p.value === preset)?.days ?? 7;
    const expiresAt = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
    setCreating(true);
    setError(null);
    try {
      const link = await automationService.createScenarioShare(runId, expiresAt);
      setLinks(prev => [link, ...prev]);
      // Copy the fresh link straight to the clipboard — the common case is
      // "create then paste."
      if (await copyText(shareUrlFor(link.token))) {
        setCopiedToken(link.token);
        setTimeout(() => setCopiedToken(t => (t === link.token ? null : t)), 1500);
      }
    } catch (err: any) {
      setError(err.message || String(err));
    } finally {
      setCreating(false);
    }
  };

  const copyLink = async (token: string) => {
    if (await copyText(shareUrlFor(token))) {
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(t => (t === token ? null : t)), 1500);
    } else {
      setError("Couldn't copy — select the URL manually.");
    }
  };

  const revokeLink = async (token: string) => {
    setError(null);
    try {
      // revokeScenarioShare treats 404 as success (already gone); any other
      // failure throws.
      await automationService.revokeScenarioShare(runId, token);
      setLinks(prev => prev.filter(l => l.token !== token));
    } catch (err: any) {
      // Revoke genuinely failed — leave the link visible so the owner knows
      // the share is still active rather than showing a false "revoked".
      setError(`Couldn't revoke link — it is still active. ${err.message || String(err)}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LinkIcon className="h-4 w-4" /> Share scenario
          </DialogTitle>
          <DialogDescription>
            Create an expiring, revocable link to <strong>{scenarioName || 'this scenario'}</strong>.
            Anyone with the link can view the trades read-only until it expires or you revoke it.
          </DialogDescription>
        </DialogHeader>

        {/* Create row */}
        <div className="flex items-end gap-2">
          <div className="flex flex-col">
            <span className="text-[10px] text-muted-foreground mb-0.5">Expires after</span>
            <Select value={preset} onValueChange={setPreset}>
              <SelectTrigger className="h-9 w-[140px]"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-popover/100 backdrop-blur-md z-[200]">
                {EXPIRY_PRESETS.map(p => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button size="sm" onClick={createLink} disabled={creating}>
            {creating
              ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating…</>
              : <><Plus className="h-4 w-4 mr-2" /> Create link</>}
          </Button>
        </div>

        {error && <div className="text-xs text-red-500">{error}</div>}

        {/* Existing links */}
        <div className="border-t pt-3">
          <div className="text-xs font-semibold text-muted-foreground mb-2">
            Active links {links.length > 0 ? `(${links.length})` : ''}
          </div>
          {loading ? (
            <div className="flex items-center text-xs text-muted-foreground">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading…
            </div>
          ) : links.length === 0 ? (
            <div className="text-xs text-muted-foreground">No active links yet.</div>
          ) : (
            <div className="flex flex-col gap-1.5 max-h-[240px] overflow-y-auto">
              {links.map(l => (
                <div
                  key={l.token}
                  className="flex items-center gap-2 text-xs border rounded px-2 py-1.5"
                >
                  <span className="flex-1 truncate font-mono" title={shareUrlFor(l.token)}>
                    …{l.token.slice(-12)}
                  </span>
                  <span className="text-muted-foreground whitespace-nowrap">
                    expires {fmtExpiry(l.expiresAt)}
                  </span>
                  <Button
                    variant="outline" size="sm" className="h-7 px-2"
                    onClick={() => copyLink(l.token)}
                    title="Copy share URL"
                  >
                    {copiedToken === l.token
                      ? <Check className="h-3.5 w-3.5 text-green-500" />
                      : <Copy className="h-3.5 w-3.5" />}
                  </Button>
                  <Button
                    variant="outline" size="sm" className="h-7 px-2"
                    onClick={() => revokeLink(l.token)}
                    title="Revoke this link"
                  >
                    <Trash2 className="h-3.5 w-3.5 text-red-500" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareScenarioDialog;
